import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';

import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { api, ModalService, PagedSearchResults, TimeService } from '@bayclubs/utils';

import { CouponApiService } from '../../../services/api/coupon-api.service';
import { camps } from '../../../models/models.generated';
import { CouponGenerateModalComponent } from '../coupon-generator/modal/coupon-generate.component.modal';
import { FiltersStorageService } from '../../../services/filters-storage.service';
import { CouponsFilter } from '../../../models/filters/coupons-filter.model';
import { FiltersHelperService } from '../../../services/filters-helper.service';
import { RosterFilter } from '../../../models/filters/roster-filter.model';

@Component({
    selector: 'app-coupons-list',
    host: { 'data-testid': 'coupons-list-page' },
    templateUrl: './coupons-list.component.html',
    styleUrls: ['./coupons-list.component.scss'],
    standalone: false
})
export class CouponsListComponent implements OnInit {
    public items: camps.CouponInfo[] = [];

    public searchResults: PagedSearchResults = new PagedSearchResults();

    public isBusy: boolean = false;

    public pageSize: number = 30;

    public searchForm: UntypedFormGroup;

    public filters: CouponsFilter;

    constructor(
        private api: CouponApiService,
        private router: Router,
        private formBuilder: UntypedFormBuilder,
        private modal: ModalService,
        private filtersHelperService: FiltersHelperService,
        private time: TimeService
    ) {
        this.filters = this.getFilter();
    }

    ngOnInit() {
        this.isBusy = true;
        this.initForm(this.filters);
    }

    onSearch(numberOfPagesToSkip: number = 0) {
        this.isBusy = true;

        this.getCampsApiPromise(numberOfPagesToSkip).subscribe(result => {
            if (numberOfPagesToSkip === 0) {
                this.items = [];
            }

            if (result.isSuccess) {
                const filter: CouponsFilter = <CouponsFilter>{
                    code: this.searchForm.controls['code'].value,
                    dateFrom: this.searchForm.controls['dateFrom'].value,
                    dateTo: this.searchForm.controls['dateTo'].value,
                    statuses: this.searchForm.controls['statuses'].value,
                    types: this.searchForm.controls['types'].value,
                    categories: this.searchForm.controls['categories'].value,
                    usageTypes: this.searchForm.controls['usageTypes'].value
                };

                this.filtersHelperService.mergeQueryParam(filter, 'Coupons');

                this.searchResults.pageNumber = result.pageNumber;
                this.searchResults.totalCount = result.totalCount;
                this.searchResults.totalPages = result.totalPages;
                this.searchResults.pages = Array<number>();

                for (var j = 1; j <= this.searchResults.totalPages; j++) {
                    this.searchResults.pages.push(j);
                }

                for (var i = 0; i < result.pageItems.length; i++) {
                    this.items.push(result.pageItems[i]);
                }
            }

            this.isBusy = false;
        });
    }

    exportToXls(): void {
        let criteria = this.prepareRequest();
        criteria.take = null;
        criteria.skip = null;

        this.api.getCouponReport(criteria);
    }

    private getCampsApiPromise(numberOfPagesToSkip: number): Observable<api.OperationPagedResult<camps.CouponInfo>> {
        let criteria = this.prepareRequest(numberOfPagesToSkip);
        return this.api.search(criteria);
    }

    private prepareRequest(numberOfPagesToSkip: number = 0): camps.CouponSearchRequest {
        const request = <camps.CouponSearchRequest>{
            take: this.pageSize,
            skip: numberOfPagesToSkip * this.pageSize,
            codeOrEmail: this.searchForm.controls['code'].value,
            dateFrom: this.searchForm.controls['dateFrom'].value,
            dateTo: this.searchForm.controls['dateTo'].value,
            statuses: this.searchForm.controls['statuses'].value,
            types: this.searchForm.controls['types'].value,
            categoryIds: this.searchForm.controls['categories'].value,
            usageTypes: this.searchForm.controls['usageTypes'].value
        };

        return request;
    }

    initForm(filter: CouponsFilter) {
        this.searchForm = this.formBuilder.group({
            code: [filter.code, []],
            dateFrom: [filter.dateFrom, []],
            dateTo: [filter.dateTo, []],
            statuses: [filter.statuses, []],
            types: [filter.types, []],
            categories: [filter.categories, []],
            usageTypes: [filter.usageTypes, []]
        });

        this.searchForm.valueChanges.pipe(debounceTime(200)).subscribe(() => {
            this.onSearch(0);
        });

        this.onSearch(0);
    }

    public onScroll() {
        if (this.searchResults.canNextPage) {
            this.onSearch(this.searchResults.nextPageNumber);
        }
    }

    createCoupon() {
        this.modal.create<CouponGenerateModalComponent>(CouponGenerateModalComponent, {}).subscribe(result => {
            if (result.value) {
                this.onViewDetails(result.value);
            }
        });
    }

    public get noItems(): boolean {
        return !this.items || this.items.length == 0;
    }

    public onViewDetails(item: camps.CouponInfo) {
        this.router.navigate(['coupons/' + item.id + '/details']);
    }

    private getFilter() {
        const nowMoment = this.time.nowInTimeZone(this.time.defaultTimezone());
        const nowBcDate = this.time.toBcDate(nowMoment);

        const defaultFilter: CouponsFilter = {
            code: null,
            dateFrom: nowBcDate,
            dateTo: null,
            statuses: [],
            categories: [],
            types: [],
            usageTypes: []
        };

        const restoredFilter = this.filtersHelperService.restoreFilter<CouponsFilter>('Coupons');

        return restoredFilter ? restoredFilter : defaultFilter;
    }
}
